import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate for redirection
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";
import logo from "../../images/logo.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { useTranslation } from 'react-i18next';

const UsersSubmenu = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  min-width: 220px;
  padding: 8px 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 9999;
  display: ${({ $open }) => ($open ? "flex" : "none")};
  flex-direction: column;

  a {
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 12px 16px;
    text-align: center;
    white-space: nowrap;
    color: #1f2937;
    text-decoration: none;
    border: none;
    margin: 0;
    background: white;
  }

  a:hover {
    background: #f3f4f6;
    color: #4e7662;
  }
`;
// Styled Components
const Header = tw.header`
  flex justify-between items-center max-w-screen-xl mx-auto mt-[30px] mb-[40px]
`;
export const NavLinks = tw.div`flex items-center gap-2	`;
export const NavLink = tw.a`
  text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-[#4e7662] hocus:text-[#4e7662]
`;
export const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-8 py-3 rounded bg-[#4e7662] text-gray-100
  hocus:bg-[#4e7662] hocus:text-gray-200 focus:shadow-outline
  border-b-0
`;
export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};

  img {
    width: 130px; /* Set your desired width */
    height: auto; /* Maintain aspect ratio */
    ${tw`mr-3`} /* Tailwind margin right */
  }
`;

const MobileNavLinksContainer = tw.nav`
  flex flex-1 items-center justify-between lg:hidden
`
export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
`;
const MobileNavLinks = motion(styled.div`
  position: absolute;
  top: 170 px; // 👈 Reduced spacing
  right: 0;
  width: 16rem;
  padding: 1.5rem;
  background-color: white;
  color: #1f2937;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 50;
`);
const MobileNavLinksList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
`;
export const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center
`;
const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  min-width: 200px;
  padding: 8px 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 100;
  display: none;
  flex-direction: column;

  a {
    display: block;
    width: 100%;
    padding: 10px 16px;
    text-align: center;
    white-space: nowrap;
    color: #1f2937;
    text-decoration: none;
    border: none;
    margin: 0;
  }

  a:hover {
    background: transparent;
    color: #4e7662;
  }
`;

const UsersDropdown = styled.div`
   position: relative;
  display: inline-flex;
  align-items: center;

  &:hover > a {
    color: #4e7662;
    border-bottom-color: #4e7662;
  }
`;

// Styled NavLink for Dropdown with Hover Color
export const NavLinkStyled = styled(NavLink)`
  ${tw`block px-4 py-2 text-gray-800 transition-colors duration-300`};
  &:hover {
    background-color: #4e7662; /* Ajoute le fond bleu personnalisé */
    color: white; /* Change la couleur du texte pour plus de visibilité */
  }
`;



export const UserButton = styled.button`
  ${tw`relative px-4 py-2 rounded-full bg-[#4e7662] text-white font-semibold mr-4 transition duration-300`};
  &:hover {
    background-color: #1e3d56; /* Nouvelle couleur au survol */
    color: #ffffff; /* Texte blanc */
  }
  &:focus {
    ${tw`outline-none`};
  }
`;


export default ({
  roundedHeaderButton = false,
  logoLink,
  links,
  className,
  collapseBreakpointClass = "lg",
}) => {
const { i18n, t } = useTranslation();

  const [dropdownOpen, setDropdownOpen] = useState(false);
const [usersSubmenuOpen, setUsersSubmenuOpen] = useState(false);
const [ordersSubmenuOpen, setOrdersSubmenuOpen] = useState(false);
  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  // New state to hold user info
  const [user, setUser] = useState(null);

  const navigate = useNavigate(); // Use navigate for redirection

  // Check if user is logged in and retrieve info from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user'); // Get the user data from localStorage
    if (userData) {
      setUser(JSON.parse(userData)); // Parse and set the user data in state
    }
  }, []);

  // Function to handle logout
  const handleLogout = () => {
    localStorage.removeItem('user'); // Clear user data from localStorage
    localStorage.removeItem('authToken'); // Optionally remove authToken or other info
    localStorage.removeItem('id');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('role');

    setUser(null);
 // Reset the user state
        localStorage.setItem('role', "supplier"); // Store admin status

    navigate('/'); // Redirect to the home page
  };

  // Default navigation links
  const defaultLinks = [
    <NavLinks key={1}>
      <NavLink href="/produits-admin"> {t('Produits')}</NavLink>
 
{/* Commandes submenu: click to open/close */}
<UsersDropdown>
  <NavLink
    href="/order-admin"
    onClick={(e) => {
      e.preventDefault();
      setOrdersSubmenuOpen((prev) => !prev);
    }}
    aria-haspopup="true"
    aria-expanded={ordersSubmenuOpen}
  >
    {t("Commandes")}
    <span style={{ fontSize: "10px", marginLeft: "5px" }}>
      {ordersSubmenuOpen ? "▲" : "▼"}
    </span>
  </NavLink>

  <UsersSubmenu $open={ordersSubmenuOpen}>
    <NavLinkStyled href="/order-admin">
      Commandes
    </NavLinkStyled>

    <NavLinkStyled href="/orderSans">
      Commandes visiteurs
    </NavLinkStyled>
  </UsersSubmenu>
</UsersDropdown>

      <NavLink href="/retraite"> {t('Retraite')}</NavLink>
      <NavLink href="/payment-admin"> {t('Paiement')}</NavLink>
   {/* Utilisateurs submenu: hover */}
     
{/* Utilisateurs submenu: click to open/close */}
<UsersDropdown>
  <NavLink
    href="/user-admin"
    onClick={(e) => {
      e.preventDefault();
      setUsersSubmenuOpen((prev) => !prev);
    }}
    aria-haspopup="true"
    aria-expanded={usersSubmenuOpen}
  >
    {t("Utilisateurs")}
    <span style={{ fontSize: "10px", marginLeft: "5px" }}>
      {usersSubmenuOpen ? "▲" : "▼"}
    </span>
  </NavLink>

<UsersSubmenu $open={usersSubmenuOpen}>
  <NavLinkStyled href="/DisInfUsers-admin">
    {t("Distributeurs_Influenceurs")}
  </NavLinkStyled>

  <NavLinkStyled href="/ClientUsers-admin">
    {t("Clients_fideles")}
  </NavLinkStyled>
</UsersSubmenu>

 </UsersDropdown>

             

  <NavLink href="/client-admin"> {t('Clients')}</NavLink>
      <NavLink href="/sales-admin">{t('sales')}</NavLink>
      <NavLink href="/orderAdmin">{t('Profits')}</NavLink>



      {user ? (
        // When user is logged in, display user's name with dropdown for Profile & Logout
        <div tw="relative inline-block">
          <UserButton onClick={() => setDropdownOpen(!dropdownOpen)}>
            {user.name} {/* Display the user's name */}
          </UserButton>
          {dropdownOpen && (
            <DropdownMenu isArabic={i18n.language === 'ar'}>
                       <NavLink
                href="/dashbord-admin"
                tw="block px-4 py-2 text-gray-800 hover:bg-gray-200"
              >
 {t('Tableau_de_bord')}
              </NavLink>
   <NavLink
                href="/OrderLP"
                tw="block px-4 py-2 text-gray-800 hover:bg-gray-200"
              >
 {t('OrderLP')}
              </NavLink>

     <NavLink
                href="/admin-announcement"
                tw="block px-4 py-2 text-gray-800 hover:bg-gray-200"
              >
 {t('Announcement')}
              </NavLink>

              <NavLink
                href="/"
                onClick={handleLogout} // Call the handleLogout function on click
                tw="block px-4 py-2 text-gray-800 hover:bg-gray-200"
              >
                 {t('Déconnexion')}
              </NavLink>
            </DropdownMenu>
          )}
        </div>
      ) : (
        // If user is not logged in, display Login and Sign Up
        <>
          {/* <NavLink href="/login" tw="lg:ml-12!">
            Connexion
          </NavLink>
          <PrimaryLink css={roundedHeaderButton && tw`rounded-full`} href="/signup">
            Inscription
          </PrimaryLink> */}
        </>
      )}
    </NavLinks>,
  ];

  const defaultLogoLink = (
    <LogoLink href="/">
<img src={logo} alt="logo" width={1000} height={1000} />
</LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <Header className={className || "header-light"}>
      <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
        {logoLink}
        {links}
      </DesktopNavLinks>

      <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
        {logoLink}
        <MobileNavLinks
          initial={{ x: "150%", display: "none" }}
          animate={animation}
          css={collapseBreakpointCss.mobileNavLinks}
        >
          {links}
        </MobileNavLinks>
        <NavToggle onClick={toggleNavbar} className={showNavLinks ? "open" : "closed"}>
          {showNavLinks ? <CloseIcon tw="w-6 h-6" /> : <MenuIcon tw="w-6 h-6" />}
        </NavToggle>
      </MobileNavLinksContainer>
    </Header>
  );
};

// Breakpoint mappings for different screen sizes
const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`,
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`,
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
};
